import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { RedirectError } from 'src/shared/error';

import type { SupporterVoicesConfig } from 'src/app/pages/petitionGamma/details/shared/api';
import { getSupporterVoices, getSupporterVoicesConfig } from 'src/app/pages/petitionGamma/details/shared/api';
import { getPetitionId } from 'src/app/shared/api/petitions';
import { differenceInDays } from 'src/app/shared/utils/dates';

import type { GammaPetitionDetailsDataQuery, GammaPetitionDetailsDataQueryVariables } from './api.graphql';
import { GammaPetitionDetailsData } from './api.graphql';
import { getContactDmConfig } from './getContactDmConfig';
import { getPetitionStrengthScore } from './getPetitionStrengthScore';
import type { PetitionDetailsPageData } from './types';
import { getPetitionShareVisuals } from './utilities/getPetitionShareVisuals';

async function resolvePetitionId(slugOrId: string, utilityContext: UtilityContext): Promise<string> {
	if (Number(slugOrId)) return slugOrId;
	return (await getPetitionId(slugOrId, utilityContext)) || '';
}

async function fetchPetitionDetails(utilityContext: UtilityContext, variables: GammaPetitionDetailsDataQueryVariables) {
	const {
		gql: { fetch },
	} = utilityContext;
	const { data } = await fetch<GammaPetitionDetailsDataQuery, GammaPetitionDetailsDataQueryVariables>({
		query: GammaPetitionDetailsData,
		variables,
		rejectOnError: true,
		// to separate query from other batched queries
		batched: false,
	});
	return data;
}

function normalizePetition(
	petition: NonNullable<GammaPetitionDetailsDataQuery['petition']>,
): PetitionDetailsPageData['petition'] {
	return {
		...petition,
		primaryDecisionMakerConnection: {
			totalCount: petition.primaryDecisionMakerConnection.totalCount,
			nodes: petition.primaryDecisionMakerConnection.edges.map((edge) => ({
				...edge.node,
				targetPetitionAssociationData: edge.associationData,
			})),
		},
		shareVisuals: getPetitionShareVisuals(petition),
	};
}

export async function getPetitionDetailsPageData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		locale,
		countryCode,
		loadBandits,
		supporterVoicesConfig,
	}: {
		locale: string;
		countryCode: string;
		loadBandits: boolean;
		supporterVoicesConfig?: SupporterVoicesConfig;
	},
): Promise<PetitionDetailsPageData> {
	const petitionId = await resolvePetitionId(slugOrId, utilityContext);
	if (!petitionId) throw new RedirectError({ url: '/?petition_not_found=true' });

	const { emailEnabled, emailBody, emailSubjectLine } = await getContactDmConfig(utilityContext);

	const config = supporterVoicesConfig || (await getSupporterVoicesConfig(utilityContext, slugOrId));

	const data = await fetchPetitionDetails(utilityContext, {
		slugOrId,
		locale,
		countryCode,
		loadBandits,
		commentsSortBy: config.sortByScoreEnabled ? 'SCORE' : 'POPULAR',
		emailEnabled,
	});

	if (!data.petition) throw new RedirectError({ url: '/?petition_not_found=true' });

	const supporterVoicesData = await getSupporterVoices(
		utilityContext,
		slugOrId,
		config,
		data.petition.videoSettingsMetadata.disableDisplay,
	);
	const petition = normalizePetition(data.petition);
	return {
		petition,
		recommendedDecisionMakers: data.recommendedDecisionMakers?.items || [],
		createdLessThanOneDayAgo: differenceInDays(new Date(), new Date(data.petition.createdAt)) < 1,
		score: getPetitionStrengthScore(data.petition),
		supporterVoicesData,
		emailDm: { emailSubjectLine, emailBody },
		mediaHitsPage: {
			isLastPage: !petition.mediaHitsConnection.pageInfo.hasNextPage,
			numberOfStarterMedia: petition.mediaHitsConnection.totalCount,
			mediaHits: petition.mediaHitsConnection.nodes,
		},
	};
}
